<template>
  <div class="security-rounds">
    <v-container class="pa-3" style="max-width: 620px;">
      <h1 class="mb-2">{{ $t('security_rounds') }}</h1>

      <ShiftAlertBanner
        v-if="isSecurityGuard"
        :activeShift="activeShift"
        :showAccessDenied="!hasActiveShift"
        :restrictAccess="true"
      />

      <v-alert v-if="securityError" type="error" dismissible class="mb-3">
        {{ securityError }}
      </v-alert>

      <template v-if="hasActiveShift">
        <!-- SIN RONDIN ACTIVO: SELECCIONAR GEOCERCA -->
        <v-card v-if="!activeRound" class="elevation-3 mb-4">
          <v-toolbar dense color="primary" dark>
            <v-toolbar-title>{{ $t('security_rounds_start') }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-progress-linear v-if="isLoading" indeterminate class="mb-3"></v-progress-linear>

            <template v-if="geocercas.length > 0">
              <v-select
                v-model="selectedGeocercaId"
                :items="geocercas"
                item-text="nombre"
                item-value="id"
                :label="$t('security_rounds_select_geocerca')"
              ></v-select>
              <v-btn
                color="primary"
                block
                large
                round
                depressed
                :disabled="!selectedGeocercaId"
                :loading="isLoading"
                @click="startRound"
              >
                <v-icon left>play_arrow</v-icon>
                {{ $t('security_rounds_start') }}
              </v-btn>
            </template>
            <v-alert v-else-if="!isLoading" type="info" :value="true">
              {{ $t('security_rounds_no_geocercas') }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- RONDIN ACTIVO -->
        <template v-else>
          <v-card class="elevation-3 mb-4">
            <v-toolbar dense color="primary" dark>
              <v-toolbar-title>{{ activeRound.geocerca_nombre }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <div class="text-xs-center mb-2">
                <span class="title font-weight-bold">{{ activeRoundVisitedCount }}/{{ activeRound.total_checkpoints }}</span>
                <span class="grey--text"> {{ $t('security_rounds_checkpoints_visited') }}</span>
              </div>
              <v-progress-linear
                :value="activeRound.porcentaje_completado"
                color="success"
                height="10"
                class="mb-3"
              ></v-progress-linear>

              <v-btn
                color="success"
                block
                round
                depressed
                @click="openCompleteDialog"
              >
                <v-icon left>check_circle</v-icon>
                {{ $t('security_rounds_complete') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <v-card class="elevation-3 mb-4">
            <v-toolbar dense color="blue-grey darken-1" dark>
              <v-toolbar-title>{{ $t('security_rounds_checkpoints') }}</v-toolbar-title>
            </v-toolbar>
            <v-progress-linear v-if="isLoadingGeocercaDetail" indeterminate class="mb-0"></v-progress-linear>

            <v-list v-if="orderedCheckpoints.length > 0" two-line>
              <template v-for="(checkpoint, index) in orderedCheckpoints">
                <v-list-tile
                  :key="checkpoint.id"
                  :ripple="!isCheckpointVisited(checkpoint.id)"
                  @click="onCheckpointClick(checkpoint)"
                >
                  <v-list-tile-avatar>
                    <v-icon :color="isCheckpointVisited(checkpoint.id) ? 'success' : 'grey'">
                      {{ isCheckpointVisited(checkpoint.id) ? 'check_circle' : 'radio_button_unchecked' }}
                    </v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ checkpoint.orden }}. {{ checkpoint.nombre }}</v-list-tile-title>
                    <v-list-tile-sub-title v-if="isCheckpointVisited(checkpoint.id)">
                      {{ $t('security_rounds_visited_at') }} {{ formatTime(getCheckpointVisit(checkpoint.id).fecha_visita) }}
                      &middot; {{ getCheckpointVisit(checkpoint.id).distancia_formateada }}
                      <span v-if="getCheckpointVisit(checkpoint.id).dentro_geocerca === false" class="error--text font-weight-bold">
                        &middot; {{ $t('security_rounds_outside_geofence') }}
                      </span>
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title v-else>{{ checkpoint.descripcion || '-' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action v-if="isCheckpointVisited(checkpoint.id) && getCheckpointVisit(checkpoint.id).foto">
                    <v-avatar size="40">
                      <img :src="getCheckpointVisit(checkpoint.id).foto" :alt="checkpoint.nombre">
                    </v-avatar>
                  </v-list-tile-action>
                </v-list-tile>
                <v-divider v-if="index < orderedCheckpoints.length - 1" :key="`div-${checkpoint.id}`"></v-divider>
              </template>
            </v-list>
          </v-card>
        </template>

        <div class="text-xs-center">
          <v-btn flat color="primary" @click="$router.push('/security/rounds/history')">
            <v-icon left small>history</v-icon>
            {{ $t('security_rounds_history') }}
          </v-btn>
        </div>
      </template>
    </v-container>

    <!-- DIALOGO DE CAPTURA DE CHECKPOINT -->
    <v-dialog v-model="showCaptureDialog" max-width="480px" persistent @input="onCaptureDialogToggle">
      <v-card v-if="currentCheckpoint">
        <v-toolbar dense color="primary" dark>
          <v-toolbar-title>{{ currentCheckpoint.nombre }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <div class="text-xs-center mb-3">
            <select v-if="devices.length > 1" v-model="camera" class="camera-select">
              <option v-for="device in devices" :key="device.deviceId" :value="device.deviceId">
                {{ device.label }}
              </option>
            </select>

            <div v-if="!capturedImg">
              <vue-web-cam
                ref="webcam"
                :device-id="deviceId"
                width="100%"
                @started="onCameraStarted"
                @error="onCameraError"
                @cameras="onCameras"
                @camera-change="onCameraChange"
              />
              <v-btn color="primary" round class="mt-2" :disabled="!cameraReady" @click="capturePhoto">
                <v-icon left>camera_alt</v-icon>
                {{ $t('security_rounds_capture_photo') }}
              </v-btn>
            </div>
            <div v-else>
              <v-img :src="capturedImg" max-height="280" contain></v-img>
              <v-btn flat small @click="retakePhoto">
                <v-icon left small>refresh</v-icon>
                {{ $t('security_rounds_retake_photo') }}
              </v-btn>
            </div>
          </div>

          <v-alert v-if="cameraError" type="error" :value="true" dense class="mb-3">
            {{ $t('security_rounds_camera_error') }}
          </v-alert>

          <v-textarea
            v-model="captureNotas"
            :label="$t('security_rounds_visit_notes')"
            rows="2"
          ></v-textarea>

          <v-alert v-if="gpsError" type="warning" :value="true" dense class="caption">
            {{ $t('security_rounds_gps_error') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeCaptureDialog">{{ $t('cancelar') }}</v-btn>
          <v-btn
            color="primary"
            :disabled="!capturedImg"
            :loading="isLoading"
            @click="submitCheckpointVisit"
          >
            {{ $t('security_rounds_send_visit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO COMPLETAR RONDIN -->
    <v-dialog v-model="showCompleteDialog" max-width="420px">
      <v-card>
        <v-card-title>{{ $t('security_rounds_complete') }}</v-card-title>
        <v-card-text>
          <v-alert
            v-if="activeRound && activeRoundVisitedCount < activeRound.total_checkpoints"
            type="warning"
            :value="true"
            dense
            class="mb-3"
          >
            {{ $t('security_rounds_incomplete_warning') }}
          </v-alert>
          <v-textarea
            v-model="completeNotas"
            :label="$t('security_rounds_final_notes')"
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showCompleteDialog = false">{{ $t('cancelar') }}</v-btn>
          <v-btn color="success" :loading="isLoading" @click="completeRound">
            {{ $t('security_rounds_complete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { WebCam } from 'vue-web-cam'
import {
  FETCH_GUARD_GEOCERCAS,
  FETCH_GEOCERCA_DETAIL,
  START_ROUND,
  FETCH_ACTIVE_ROUND,
  REGISTER_CHECKPOINT_VISIT,
  COMPLETE_ROUND,
  VALIDATE_SHIFT_STATUS
} from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'
import ShiftAlertBanner from '../../../components/security/ShiftAlertBanner.vue'
import { hasExceededMaxShiftDuration } from '../../../services/shiftValidationService'

export default {
  name: 'SecurityRounds',
  components: {
    ShiftAlertBanner,
    'vue-web-cam': WebCam
  },
  data() {
    return {
      selectedGeocercaId: null,
      isLoadingGeocercaDetail: false,
      showCaptureDialog: false,
      currentCheckpoint: null,
      camera: null,
      deviceId: null,
      devices: [],
      cameraReady: false,
      cameraError: false,
      capturedImg: '',
      captureNotas: '',
      gpsError: false,
      currentPosition: null,
      showCompleteDialog: false,
      completeNotas: ''
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getGuardGeocercas',
      'getGeocercaDetail',
      'getActiveRound',
      'getActiveShift'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    geocercas() {
      return this.getGuardGeocercas
    },
    activeRound() {
      return this.getActiveRound
    },
    activeRoundVisitedCount() {
      if (!this.activeRound) return 0
      // La documentación usa "checkpoints_visitados" en unos endpoints y
      // "checkpoints_completados" en otros; soportamos ambos por las dudas.
      if (this.activeRound.checkpoints_visitados !== undefined) return this.activeRound.checkpoints_visitados
      if (this.activeRound.checkpoints_completados !== undefined) return this.activeRound.checkpoints_completados
      return Array.isArray(this.activeRound.visitas) ? this.activeRound.visitas.length : 0
    },
    orderedCheckpoints() {
      const detail = this.getGeocercaDetail
      if (!detail || !Array.isArray(detail.checkpoints)) return []
      return [...detail.checkpoints].sort((a, b) => a.orden - b.orden)
    },
    isSecurityGuard() {
      return this.getProfile && this.getProfile.roll === 9
    },
    activeShift() {
      return this.getActiveShift
    },
    hasActiveShift() {
      return !!this.activeShift && !hasExceededMaxShiftDuration(this.activeShift.start_time)
    }
  },
  watch: {
    camera(id) {
      this.deviceId = id
    }
  },
  methods: {
    fetchInitialData() {
      this.$store.dispatch(`security/${FETCH_ACTIVE_ROUND}`, this.getToken)
        .then(round => {
          if (round) {
            this.fetchGeocercaDetail(round.geocerca)
          } else {
            this.fetchGeocercas()
          }
        })
    },
    fetchGeocercas() {
      this.$store.dispatch(`security/${FETCH_GUARD_GEOCERCAS}`, this.getToken).catch(() => {})
    },
    fetchGeocercaDetail(geocercaId) {
      this.isLoadingGeocercaDetail = true
      this.$store.dispatch(`security/${FETCH_GEOCERCA_DETAIL}`, { token: this.getToken, geocercaId })
        .finally(() => {
          this.isLoadingGeocercaDetail = false
        })
    },
    startRound() {
      if (!this.selectedGeocercaId) return
      this.$store.dispatch(`security/${START_ROUND}`, {
        token: this.getToken,
        geocercaId: this.selectedGeocercaId
      })
        .then(round => {
          this.fetchGeocercaDetail(round.geocerca)
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_rounds_error_starting'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    isCheckpointVisited(checkpointId) {
      return !!this.getCheckpointVisit(checkpointId)
    },
    getCheckpointVisit(checkpointId) {
      if (!this.activeRound || !Array.isArray(this.activeRound.visitas)) return null
      return this.activeRound.visitas.find(v => v.checkpoint === checkpointId) || null
    },
    onCheckpointClick(checkpoint) {
      if (this.isCheckpointVisited(checkpoint.id)) return
      this.currentCheckpoint = checkpoint
      this.capturedImg = ''
      this.captureNotas = ''
      this.cameraError = false
      this.gpsError = false
      this.currentPosition = null
      this.showCaptureDialog = true
      this.requestGpsPosition()
    },
    requestGpsPosition() {
      if (!navigator.geolocation) {
        this.gpsError = true
        return
      }
      navigator.geolocation.getCurrentPosition(
        position => {
          this.currentPosition = position.coords
          this.gpsError = false
        },
        () => {
          this.gpsError = true
        },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    },
    onCameraStarted() {
      this.cameraReady = true
      this.cameraError = false
    },
    onCameraError() {
      this.cameraReady = false
      this.cameraError = true
    },
    onCameras(cameras) {
      this.devices = cameras
      if (cameras.length > 0 && !this.deviceId) {
        this.camera = cameras[0].deviceId
        this.deviceId = cameras[0].deviceId
      }
    },
    onCameraChange(deviceId) {
      this.deviceId = deviceId
      this.camera = deviceId
    },
    capturePhoto() {
      if (!this.$refs.webcam) return
      this.capturedImg = this.$refs.webcam.capture()
      if (!this.currentPosition) this.requestGpsPosition()
    },
    retakePhoto() {
      this.capturedImg = ''
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    onCaptureDialogToggle(isOpen) {
      if (!isOpen && this.$refs.webcam) {
        this.$refs.webcam.stop()
      }
    },
    closeCaptureDialog() {
      this.showCaptureDialog = false
      this.currentCheckpoint = null
    },
    submitCheckpointVisit() {
      if (!this.capturedImg || !this.currentCheckpoint) return
      if (!this.currentPosition) {
        this.gpsError = true
        return
      }

      const foto = this.dataURLtoFile(this.capturedImg, `checkpoint_${this.currentCheckpoint.id}_${Date.now()}.png`)

      this.$store.dispatch(`security/${REGISTER_CHECKPOINT_VISIT}`, {
        token: this.getToken,
        roundId: this.activeRound.id,
        checkpointId: this.currentCheckpoint.id,
        latitude: this.currentPosition.latitude.toFixed(6),
        longitude: this.currentPosition.longitude.toFixed(6),
        foto,
        notas: this.captureNotas
      })
        .then(visita => {
          this.$store.commit('snackbar_change_message', this.$t('security_rounds_visit_registered'))
          this.$store.commit('snackbar_change_color', visita.dentro_geocerca === false ? 'warning' : 'success')
          this.$store.commit('snackbar_activate_true')
          this.closeCaptureDialog()
          this.$store.dispatch(`security/${FETCH_ACTIVE_ROUND}`, this.getToken)
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_rounds_error_registering'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    openCompleteDialog() {
      this.completeNotas = ''
      this.showCompleteDialog = true
    },
    completeRound() {
      this.$store.dispatch(`security/${COMPLETE_ROUND}`, {
        token: this.getToken,
        roundId: this.activeRound.id,
        notas: this.completeNotas
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', this.$t('security_rounds_completed_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.showCompleteDialog = false
          this.selectedGeocercaId = null
          this.fetchGeocercas()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_rounds_error_completing'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    formatTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleTimeString(this.$i18n.locale, { hour: '2-digit', minute: '2-digit' })
    },
    validateAndFetch() {
      if (!this.isSecurityGuard) {
        this.$router.push('/')
        return
      }
      this.$store.dispatch(`security/${VALIDATE_SHIFT_STATUS}`)
      this.fetchInitialData()
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.validateAndFetch()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.validateAndFetch()
    }
  }
}
</script>

<style scoped>
.mb-2 {
  margin-bottom: 12px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mb-4 {
  margin-bottom: 28px;
}
.camera-select {
  width: 100%;
  margin-bottom: 8px;
}
</style>
